// src/utils/dashboard/dashboardDataAdapter.ts

import type { CurrentValuesResponse, WindowsResponse } from "@/types/dashboards/dashboardCurrent";
import { TIME_WINDOWS } from "@/constants/metrics";

export type GenericRow = Record<string, unknown>;

const windowKeyToUiSuffix = (win: string): string => {
  const k = win.toLowerCase();
  return TIME_WINDOWS.API_TO_UI[k as keyof typeof TIME_WINDOWS.API_TO_UI] || k.toUpperCase();
};

const ensureRow = (
  byTicker: Map<string, GenericRow>,
  tokenId: number,
  ticker: string,
  name?: string | null,
  color?: string | null,
  logoUrl?: string | null
): GenericRow => {
  let row = byTicker.get(ticker);
  if (!row) {
    row = { tokenId, tokenName: ticker, tokenSymbol: ticker };
    byTicker.set(ticker, row);
  }
  if (name) row["tokenName"] = name;
  if (ticker) row["tokenSymbol"] = ticker;
  if (color) row["tokenColor"] = color;
  if (logoUrl) row["logoUrl"] = logoUrl;
  return row;
};

export function adaptMetricsRows(
  current: CurrentValuesResponse,
  rollups: WindowsResponse
): GenericRow[] {
  const rowsByTicker = new Map<string, GenericRow>();

  // Non-windowed/current
  for (const t of current.tokens) {
    const row = ensureRow(rowsByTicker, t.token_id, t.ticker, t.name ?? undefined, t.color ?? undefined, t.logo_url ?? undefined);
    for (const [k, v] of Object.entries(t.metrics)) {
      if (v == null) continue;
      const field = k;
      row[field] = v as number;
    }
  }

  // Windowed rollups
  for (const t of rollups.tokens) {
    const row = ensureRow(rowsByTicker, t.token_id, t.ticker, t.name ?? undefined, t.color ?? undefined, t.logo_url ?? undefined);
    for (const metric of t.metrics) {
      const field = metric.metric_key;
      const windows = metric.windows || {};
      for (const [winKey, winVal] of Object.entries(windows)) {
        const suffix = windowKeyToUiSuffix(String(winKey));
        const value = (winVal as { value?: number | null } | undefined)?.value ?? null;
        row[`${field}_${suffix}`] = value as number | null;
      }
    }
  }

  return Array.from(rowsByTicker.values());
}

export function adaptRatiosRows(
  ratios: CurrentValuesResponse
): GenericRow[] {
  const rowsByTicker = new Map<string, GenericRow>();
  for (const t of ratios.tokens) {
    const row = ensureRow(rowsByTicker, t.token_id, t.ticker, t.name ?? undefined, t.color ?? undefined, t.logo_url ?? undefined);
    for (const [k, v] of Object.entries(t.metrics)) {
      if (v == null) continue;
      const field = k;
      row[field] = v as number;
    }
  }
  return Array.from(rowsByTicker.values());
}

export function adaptPercentageChangesRows(
  changes: WindowsResponse
): GenericRow[] {
  const rowsByTicker = new Map<string, GenericRow>();
  for (const t of changes.tokens) {
    const row = ensureRow(rowsByTicker, t.token_id, t.ticker, t.name ?? undefined, t.color ?? undefined, t.logo_url ?? undefined);
    for (const metric of t.metrics) {
      const field = metric.metric_key;
      const windows = metric.windows || {};
      for (const [winKey, winVal] of Object.entries(windows)) {
        const suffix = windowKeyToUiSuffix(String(winKey));
        const raw = (winVal as { value?: number | null } | undefined)?.value ?? null;
        const value = raw == null ? null : (Number(raw) * 100);
        row[`${field}_${suffix}`] = value as number | null;
      }
    }
  }
  // No longer merging market cap into percentage changes rows
  return Array.from(rowsByTicker.values());
}


export function adaptRollupsRows(
  rollups: WindowsResponse
): GenericRow[] {
  const rowsByTicker = new Map<string, GenericRow>();
  for (const t of rollups.tokens) {
    const row = ensureRow(
      rowsByTicker,
      (t as unknown as { token_id: number }).token_id,
      (t as unknown as { ticker: string }).ticker,
      (t as unknown as { name?: string | null }).name ?? undefined,
      (t as unknown as { color?: string | null }).color ?? undefined,
      (t as unknown as { logo_url?: string | null }).logo_url ?? undefined
    );
    type MetricWindowsLoose = { metric_key: string; windows?: Record<string, { value?: number | null }> };
    const rawMetrics = (t as unknown as { metrics: unknown }).metrics as unknown;
    let entries: MetricWindowsLoose[] = [];
    if (Array.isArray(rawMetrics)) {
      entries = rawMetrics as MetricWindowsLoose[];
    } else if (rawMetrics && typeof rawMetrics === 'object') {
      entries = Object.entries(rawMetrics as Record<string, unknown>).map(([metric_key, windows]) => ({
        metric_key,
        windows: (windows as { [k: string]: { value?: number | null } }) ?? {},
      }));
    }

    for (const metric of entries) {
      const field = metric.metric_key;
      const windows = metric.windows || {};
      for (const [winKey, winVal] of Object.entries(windows)) {
        const suffix = windowKeyToUiSuffix(String(winKey));
        // Support various window value shapes: number | { value } | { sum } | { avg } | { end }
        let value: number | null = null;
        const w = winVal as unknown as { value?: number | null; sum?: number | null; avg?: number | null; end?: number | null } | number | null | undefined;
        if (typeof w === 'number') {
          value = w;
        } else if (w && typeof w === 'object') {
          value = (w.value ?? w.sum ?? w.avg ?? w.end ?? null) as number | null;
        }
        row[`${field}_${suffix}`] = value as number | null;
      }
    }
  }
  return Array.from(rowsByTicker.values());
}


